import React, { useEffect, useRef, useState } from "react";
import { CheckboxAtom, Typography } from "get-life-storybook-ts";
import {
  buildConsentEntry,
  isConsentCaptureEnabled,
  markHealthConsentInSession,
} from "../../utils/consentCapture";
import {
  flushPendingConsents,
  recordConsentsSafely,
} from "../../api/request/Consents/Consents.service";
import { consentText, privacyPolicyUrl, TranslateFn } from "./consentTexts";
import "./HealthDataConsentGate.scss";

export interface HealthDataConsentGateProps {
  leadId?: string;
  token?: string;
  translate: TranslateFn;
  onConsented: () => void;
  domain?: string;
}

/**
 * Paso previo al cuestionario de salud: casilla separada, no premarcada, de
 * consentimiento explícito para el tratamiento de datos de salud (RGPD art. 9).
 * Sin marcarla no se puede continuar. Al marcarla se registra el consentimiento
 * (`POST /api/lead/{ulid}/consents`) sin bloquear nunca el funnel.
 */
const HealthDataConsentGate: React.FC<HealthDataConsentGateProps> = ({
  leadId,
  token,
  translate,
  onConsented,
  domain,
}) => {
  const [checked, setChecked] = useState<boolean>(false);
  const recordedRef = useRef<boolean>(false);

  const title = consentText(translate, "title", domain);
  const text = consentText(translate, "text", domain);
  const policyLink = consentText(translate, "policyLink", domain);
  const continueLabel = consentText(translate, "continue", domain);
  const policyUrl = privacyPolicyUrl(domain);

  // Consentimientos del calculador que quedaron pendientes por falta de token.
  useEffect(() => {
    flushPendingConsents(leadId, token);
  }, [leadId, token]);

  const handleChange = (value: boolean) => {
    setChecked(value);
    if (!value || recordedRef.current || !isConsentCaptureEnabled()) return;
    recordedRef.current = true;
    buildConsentEntry("health_data", true, text)
      .then((entry) => recordConsentsSafely(leadId, token, [entry]))
      .catch(() => {
        // nunca bloquea el funnel
      });
  };

  const handleContinue = () => {
    if (!checked) return;
    markHealthConsentInSession(leadId);
    onConsented();
  };

  return (
    <section className="health-consent" aria-labelledby="health-consent-title">
      <Typography
        variant="H3"
        weight="700"
        text=""
        className="health-consent__title"
      >
        <span id="health-consent-title">{title}</span>
      </Typography>
      <div className="health-consent__checkbox">
        <CheckboxAtom
          id="health-data-consent"
          isLegal={true}
          checked={checked}
          onValueChange={handleChange}
          dataCy="health-data-consent"
          label={
            <span className="health-consent__label">
              {text}{" "}
              <a href={policyUrl} target="_blank" rel="noreferrer">
                {policyLink}
              </a>
            </span>
          }
        />
      </div>
      <div className="health-consent__actions">
        <button
          type="button"
          className="health-consent__continue"
          disabled={!checked}
          onClick={handleContinue}
        >
          {continueLabel}
        </button>
      </div>
    </section>
  );
};

export default HealthDataConsentGate;
